<script setup>
// §8.2 CardHeader — title (Poppins 15/600) + optional chip + optional meta +
// right-aligned action link. Sentence case (§9).
//
// `eyebrow` swaps the title to the small-caps muted treatment used for
// secondary/summary cards ("At a glance", "Approvals", "Facts" panels) —
// still sentence-cased content, just a quieter visual weight than a primary
// content card's title.
//
// `description` adds a muted line below the title (a card's one-sentence
// explanation, e.g. "They'll get an email invite and set their own
// password."). `divider` draws a rule under the whole header block,
// separating it from the card's body — used together wherever a card's
// header carries its own explanatory copy.
defineProps({
  title: { type: String, required: true },
  meta: { type: String, default: null },
  eyebrow: { type: Boolean, default: false },
  description: { type: String, default: null },
  divider: { type: Boolean, default: false },
})
</script>

<template>
  <div class="l-cardheader-wrap" :class="{ divider }">
    <div class="l-cardheader">
      <div class="left">
        <div class="title-row">
          <h3 class="t" :class="{ eyebrow }">{{ title }}</h3>
          <span v-if="meta" class="n">{{ meta }}</span>
          <slot name="chip" />
        </div>
        <p v-if="description" class="desc">{{ description }}</p>
      </div>
      <div v-if="$slots.action" class="right">
        <slot name="action" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-cardheader-wrap.divider {
  padding-bottom: var(--sp-4);
  border-bottom: 1px solid var(--line);
}
.l-cardheader {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp-3);
}
.left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.t {
  font-family: var(--font-display);
  font-size: var(--text-card);
  font-weight: 600;
  color: var(--text);
}
.t.eyebrow {
  font-family: var(--font-ui);
  font-size: var(--text-chip);
  font-weight: 600;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.n {
  font-size: 12px;
  color: var(--text-3);
}
.desc {
  font-size: var(--text-sm);
  color: var(--text-2);
  line-height: 1.45;
}
.right {
  flex: none;
}
</style>
